import React from "react";
import { Shield, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import LetterProgressBar, { syShort } from "@/components/LetterProgressBar";
import { PATH_HOW, pathSummary, subsequentPaths } from "@/lib/letterPolicy";

export function statusTitle(s) {
  if (!s.first_letter_earned) return "Working toward First Letter";
  const n = s.additional_awards || 0;
  return n ? `First Letter + ${n} subsequent letter${n === 1 ? "" : "s"}` : "First Letter earned";
}

function Tile({ done, title, rule, chip, testId }) {
  return (
    <div className={`rounded-lg border px-3 py-2 transition-colors ${done ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={testId}>
      <p className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider ${done ? "text-amber-700" : "text-slate-400"}`}>
        {done && <Check className="h-3 w-3" />}{title}
        {chip && <span className="ml-auto rounded-full bg-amber-200 px-1.5 text-[10px] text-amber-800" data-testid={`${testId}-chip`}>{chip}</span>}
      </p>
      <p className={`text-xs font-semibold leading-tight ${done ? "text-violet-900" : "text-slate-500"}`}>{rule}</p>
    </div>
  );
}

// Single lettering status card: status, cumulative points, next-award progress and policy thresholds.
export function LetterStatusCard({ status }) {
  if (!status) return null;
  const p = status.policy || {};
  const extra = status.additional_awards || 0;
  return (
    <Card className="overflow-hidden border-violet-200 bg-white p-6 shadow-sm" data-testid="letter-status-card">
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 ring-1 ring-violet-200"><Shield className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-wider text-violet-700">Lettering Status</p>
          <p className="mt-0.5 font-heading text-2xl font-extrabold text-violet-950" data-testid="letter-status-title">{statusTitle(status)}</p>
          <p className="mt-1 text-sm text-slate-600">
            <span className="font-bold tabular-nums text-slate-900" data-testid="letter-total-points">{status.total_approved_points}</span> cumulative approved points
            <span className="text-slate-400"> · </span><span className="tabular-nums" data-testid="letter-year-points">{status.points_this_year}</span> this school year
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${status.good_standing ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid="good-standing-badge">
          {status.good_standing ? "Good standing" : "Not in good standing"}
        </span>
      </div>
      <LetterProgressBar status={status} className="mt-4" />
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="letter-thresholds">
        <Tile done={status.first_letter_earned} title="First Letter" rule={pathSummary(p.first)} testId="letter-threshold-first"
          chip={status.first_letter_earned ? `${syShort(status.first_letter_year)} · ${PATH_HOW[status.first_letter_path] || "earned"}` : null} />
        <Tile done={extra > 0} title="Subsequent Letter" testId="letter-threshold-additional"
          rule={!status.first_letter_earned ? `${pathSummary(subsequentPaths(p))} · after First Letter`
            : status.additional_eligible_this_year === false ? `${pathSummary(subsequentPaths(p))} · starts ${syShort(status.additional_eligible_from)}` : pathSummary(subsequentPaths(p))}
          chip={extra > 0 ? `earned ${extra}×` : null} />
      </div>
      <p className="mt-2 text-[11px] text-slate-400" data-testid="letter-any-path-note">Meeting any one path earns the award · one award per school year.</p>
    </Card>
  );
}

export default LetterStatusCard;
